/**
 * Stable row-hash helper.
 *
 * Why SHA-256:
 *   These hashes persist into shop_history_*.row_hash for the
 *   lifetime of the imported history. A shop with a 10-year DMS
 *   export can have hundreds of thousands to millions of repair
 *   orders, each with multiple jobs — easily 5M+ row hashes per
 *   tenant. At those cardinalities a 32-bit hash (FNV-1a, CRC32,
 *   etc.) has near-certain birthday-paradox collisions
 *   (50% collision around 77K rows on a 2^32 space). SHA-256 gives
 *   us 256-bit output and 2^128 collision resistance — vastly more
 *   than the dataset will ever need, and it removes "is this a
 *   real duplicate or a hash collision?" as a failure mode we'd
 *   ever have to investigate.
 *
 * Why a custom canonical serializer:
 *   JSON.stringify preserves insertion order, so two equivalent
 *   objects with different key-orders would produce different
 *   hashes. We sort keys recursively so re-importing the same data
 *   from a different parser path (or after a non-semantic field
 *   reorder) still collides in staging dedupe.
 *
 * Hash format:
 *   `v2:` + 64-char lowercase hex. The `v2:` prefix replaces the
 *   `v1:` (FNV-1a 32) used during the Phase 2.1 first draft. We
 *   bumped the version rather than coexisting because no v1 hashes
 *   have been persisted to any database — Phase 2.2 migrations are
 *   not applied yet. Future algorithm changes get `v3:` and so on.
 *
 * Null/undefined/empty-string normalization:
 *   These are folded to the single token `\0` before hashing so
 *   "missing email" hashes the same regardless of which absence the
 *   parser produced. Strings are trimmed; lower-cased only by the
 *   per-entity helpers below (where case-insensitivity is
 *   semantically correct — email, VIN, plate, state).
 */

import { sha256Hex } from './sha256';

const ROW_HASH_VERSION = 'v2';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function canonicalize(value: unknown): string {
  if (value === null || value === undefined) return '\0';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length === 0 ? '\0' : `s:${trimmed}`;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '\0';
    return `n:${value}`;
  }
  if (typeof value === 'boolean') return value ? 'b:1' : 'b:0';
  if (Array.isArray(value)) {
    return `a:[${value.map(canonicalize).join(',')}]`;
  }
  if (isPlainObject(value)) {
    const keys = Object.keys(value).sort();
    const parts: string[] = [];
    for (const k of keys) {
      parts.push(`${k}=${canonicalize(value[k])}`);
    }
    return `o:{${parts.join(',')}}`;
  }
  return `?:${String(value)}`;
}

/**
 * Compute the stable row hash for an object. The object's own field
 * order does NOT affect the result. Callers should restrict the
 * object to identity-bearing fields only (omit derived/joined
 * columns) — see the per-entity helpers below for the canonical
 * field set per entity.
 */
export function computeRowHash(identity: Record<string, unknown>): string {
  return `${ROW_HASH_VERSION}:${sha256Hex(canonicalize(identity))}`;
}

/**
 * Identity field set used to hash a customer row. Two parsed rows
 * with the same `externalCustomerId` will collide as long as the
 * other identifying fields are also unchanged — intentional: a
 * customer whose phone number changed in the source export
 * produces a NEW hash, so the staging-dedupe layer will treat it
 * as a meaningful update rather than a duplicate.
 */
export function hashCustomer(input: {
  externalCustomerId: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  email: string | null;
  phone: string | null;
}): string {
  return computeRowHash({
    externalCustomerId: input.externalCustomerId,
    firstName: input.firstName,
    lastName: input.lastName,
    companyName: input.companyName,
    email: input.email?.toLowerCase() ?? null,
    phone: input.phone,
  });
}

export function hashVehicle(input: {
  externalVehicleId: string;
  vin: string | null;
  licensePlate: string | null;
  licenseState: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
}): string {
  return computeRowHash({
    externalVehicleId: input.externalVehicleId,
    vin: input.vin?.toUpperCase() ?? null,
    licensePlate: input.licensePlate?.toUpperCase() ?? null,
    licenseState: input.licenseState?.toUpperCase() ?? null,
    year: input.year,
    make: input.make,
    model: input.model,
  });
}

export function hashRepairOrder(input: {
  externalRepairOrderId: string;
  openedAt: string;
  totalCents: number | null;
  externalCustomerId: string | null;
  externalVehicleId: string | null;
}): string {
  return computeRowHash({
    externalRepairOrderId: input.externalRepairOrderId,
    openedAt: input.openedAt,
    totalCents: input.totalCents,
    externalCustomerId: input.externalCustomerId,
    externalVehicleId: input.externalVehicleId,
  });
}

export function hashJob(input: {
  repairOrderRowHash: string;
  lineNumber: number;
  description: string;
  opCode: string | null;
  laborCents: number | null;
  partsCents: number | null;
}): string {
  return computeRowHash({
    repairOrderRowHash: input.repairOrderRowHash,
    lineNumber: input.lineNumber,
    description: input.description,
    opCode: input.opCode,
    laborCents: input.laborCents,
    partsCents: input.partsCents,
  });
}
